import React, { createRef, useCallback, useState } from "react";

import { useMediaQuery } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import {
  submitConsignmentForm,
  toggleConsignmentModalSection,
} from "actions/modals/consignment";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { MultiButton } from "components/Button";
import ConsignmentForm from "components/ConsignmentForm";
import { SecondaryButton } from "components/Form";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import {
  SaleyardPermission,
  ConsignmentPermission,
} from "constants/permissions";
import { ConsignableSaleTypes } from "constants/sale";
import { Settings } from "constants/settings";

import { getLivestockSaleId } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  selectPropertyEnrichedBusinessByBusinessIdLookup,
  getCarrierCharges,
  getConsignmentIdsByCarrierChargeId,
  getConsignmentsByVendorNumber,
  getIsSaleyardAdmin,
  getSelectableBranches,
  selectRoleCurrentDeploymentSales,
  getIsConsigningAgentAndSaleUnlocked,
  getHasWriteAccessInCurrentSale,
  getConsignmentById,
  selectCanSeeOtherDeployments,
  getIsLivestockAgent,
  getContextByModalType,
  getSaleById,
  getReceivalLotIdsByConsignmentId,
  getCurrentSaleyard,
} from "selectors";

import {
  useHasAddAttachmentWithoutImagePermission,
  useHasBillingPermission,
  useHasPermission,
  useTheme,
  useTranslatedSaleTypeText,
} from "hooks";
import { computeInitialValues } from "sagas/modals/util";

const Consignment = ({ saleId, onClose }) => {
  const dispatch = useDispatch();
  const formRef = createRef(null);

  const modalContext =
    useSelector(getContextByModalType(ModalTypes.Consignment)) || {};

  const { consignmentId, showSaleLots, receivalLotId, section } = modalContext;

  const selectedSale = useSelector(getSaleById(saleId));
  const businesses = useSelector(
    selectPropertyEnrichedBusinessByBusinessIdLookup,
  );
  const consignmentsByCarrierChargeId = useSelector(
    getConsignmentIdsByCarrierChargeId,
  );
  const charges = useSelector(getCarrierCharges);
  const consignment = useSelector(getConsignmentById(consignmentId));
  const consignmentsByVendorNumber = useSelector(getConsignmentsByVendorNumber);
  const branches = useSelector(getSelectableBranches);
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const restrictConsignedFrom =
    useSelector(selectRoleCurrentDeploymentSales).length === 0;

  const hasAddAttachmentWithoutImagePermission =
    useHasAddAttachmentWithoutImagePermission();
  const theme = useTheme();
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const saleTypeText = useTranslatedSaleTypeText("Consignment");

  const saleyard = useSelector(getCurrentSaleyard);
  const receivalLotIds = useSelector(
    getReceivalLotIdsByConsignmentId(consignmentId),
  );
  const hasReceivalLotPermission = hasPermission(
    saleyard,
    SaleyardPermission.featureReceivalLots,
  );

  const isAttachmentShortCut =
    window.location.pathname.indexOf("attachments") > -1;

  const isEdit = Boolean(consignmentId);
  const titleActionString = isEdit ? "Edit" : "Create";

  const defaults = {
    vendor_id: selectedSale.default_vendor_id,
    vendor_property_id: selectedSale.default_vendor_property_id,
  };

  const mapConsignmentToFormValues = consignment => ({
    ...consignment,
    receivalLotIds: hasReceivalLotPermission ? receivalLotIds : undefined,
  });

  const values = computeInitialValues(
    defaults,
    modalContext,
    consignment,
    mapConsignmentToFormValues,
  );

  let carrierName = "";
  let associatedCarrierChargesCount = 0;

  // Null coalesce operator used to prevent crash when opening consignment modal
  // before API request has come back. E.g. Resuming from sleep
  if (values?.carrierChargeId) {
    const selectedCarrierCharges = charges[values.carrierChargeId];
    values.carrierCharge = selectedCarrierCharges;

    const carrierBusiness = businesses[selectedCarrierCharges.carrier];
    carrierName = carrierBusiness?.name;
    // 0 or one less than the length, as this is other consignments
    associatedCarrierChargesCount = Math.max(
      consignmentsByCarrierChargeId[values.carrierChargeId].length - 1,
      0,
    );
  }

  const hasChangeConsignmentPermission = useHasPermission(
    getConsignmentById(consignmentId),
    ConsignmentPermission.update,
  );

  const hasWriteAccessInCurrentSale = useSelector(
    state =>
      getHasWriteAccessInCurrentSale(state) ||
      getIsConsigningAgentAndSaleUnlocked(state),
  );

  const readOnly =
    !hasWriteAccessInCurrentSale || (isEdit && !hasChangeConsignmentPermission);

  const onSubmit = useCallback(
    values => {
      dispatch(submitConsignmentForm(consignmentId, values, !consignmentId));
    },
    [dispatch, consignmentId],
  );

  function submitForm() {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  }

  const fullScreen = useMediaQuery(`(max-width: ${theme.breakpoints[1]}px)`);

  const isLivestockAgent = useSelector(getIsLivestockAgent);

  const canSeeOtherDeployments = useSelector(selectCanSeeOtherDeployments);

  const hasBillingPermission = useHasBillingPermission();

  const isConsignableSale = ConsignableSaleTypes.includes(
    selectedSale.sale_type,
  );

  const changeSection = useCallback(
    section => dispatch(toggleConsignmentModalSection(section)),
    [dispatch],
  );

  // allow read only submit because
  // - users with billing permissions can edit the consignments vendor
  // - or if consign from field is available it also editable
  const canSubmitWithReadOnly =
    hasBillingPermission ||
    (isConsignableSale && canSeeOtherDeployments && isLivestockAgent);

  const submitButtons = [
    {
      dataTour: "submit",
      onClick: submitForm,
      disabled: (readOnly && !canSubmitWithReadOnly) || !isSubmitEnabled,
      title: "Save",
    },
    hasAddAttachmentWithoutImagePermission &&
      !receivalLotId &&
      !isEdit && {
        dataTour: "submitWithNVD",
        onClick: submitForm,
        title: "Save + NVD",
        disabled:
          !isSubmitEnabled ||
          isEdit ||
          hasAddAttachmentWithoutImagePermission ||
          receivalLotId,
      },
    !isEdit && {
      dataTour: "submitAddAnother",
      onClick: submitForm,
      title: "Save + add another",
      disabled: (readOnly && !canSubmitWithReadOnly) || !isSubmitEnabled,
    },
  ].filter(Boolean);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      fullScreen={fullScreen}
      data-tour="dialog"
    >
      <DialogTitle onClose={onClose}>
        {isEdit && (
          <AuditLogLink
            auditLogType={AuditLogTypes.CONSIGNMENT}
            dataId={consignmentId}
            returnTo={window.location.hash}
          />
        )}
        &nbsp;
        {titleActionString} {saleTypeText}
        <AdminLink model="consignment" cbid={consignmentId} />
      </DialogTitle>
      <DialogContent dividers form>
        <WaitForSync
          requiredData={[
            ApiModel.BUSINESSES,
            ApiModel.PROPERTIES,
            ApiModel.NOMINATIONS,
            ApiModel.SALE_LOTS,
            ApiModel.DRAFTING_INFORMATION,
            ApiModel.RECEIVAL_LOTS,
            ApiModel.AUCTION_PENS,
          ]}
        >
          <ConsignmentForm
            carrierName={carrierName}
            section={section}
            changeSection={changeSection}
            consignmentId={consignmentId}
            livestockSaleId={getLivestockSaleId()}
            handleClose={onClose}
            consignmentsByVendorNumber={consignmentsByVendorNumber}
            values={values}
            onSubmit={onSubmit}
            isCreate={!isEdit}
            showSaleLots={showSaleLots && !isAttachmentShortCut}
            selectedSaleType={selectedSale.sale_type}
            branches={branches}
            formRef={formRef}
            setIsSubmitEnabled={setIsSubmitEnabled}
            associatedCarrierChargesCount={associatedCarrierChargesCount}
            requiresAgency={isSaleyardAdmin && !isEdit}
            restrictConsignedFrom={restrictConsignedFrom}
            readOnly={readOnly}
          />
        </WaitForSync>
      </DialogContent>

      <DialogActions>
        <div className="flex flex-1">
          <div className="flex flex-1">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          </div>
          <div className="flex flex-1">
            <MultiButton
              stickySetting={Settings.createConsignmentFormSubmit}
              buttons={submitButtons}
              fullWidth
            />
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default Consignment;
